import axios from 'axios';
import axiosInstance from './AxiosService';

const roleService = {
    // Audit Roles
    fetchRoles: async () => {
        try {
            console.log("Fetching roles from API...");
            const response = await axiosInstance.get('audit-roles/');
            console.log("Raw response from audit-roles endpoint:", response);
            
            if (response && response.data) {
                // Log the response data structure
                console.log("Role data structure:", {
                    type: typeof response.data,
                    isArray: Array.isArray(response.data),
                    length: Array.isArray(response.data) ? response.data.length : 'N/A',
                    keys: typeof response.data === 'object' ? Object.keys(response.data) : 'N/A'
                });
                
                // For Django auth groups, we should be getting an array of objects with 'id' and 'name'
                let roleData = response.data;
                
                // Check if the response contains a 'results' property (for paginated responses)
                if (!Array.isArray(roleData) && roleData.results && Array.isArray(roleData.results)) {
                    console.log("Using paginated results array");
                    roleData = roleData.results;
                }
                
                // Ensure we have an array of roles
                if (!Array.isArray(roleData)) {
                    console.warn("Role data is not an array, will convert to array:", roleData);
                    if (typeof roleData === 'object') {
                        roleData = [roleData]; // Convert single object to array
                    } else {
                        roleData = []; // Empty array for unexpected format
                    }
                }
                
                // Enhanced logging for debugging
                console.log(`Processing ${roleData.length} roles from API`);
                
                // Check first few items to see their structure
                if (roleData.length > 0) {
                    console.log("First role example:", roleData[0]);
                    
                    // Check if we have the expected Django group properties
                    const hasDjangoProps = roleData[0].hasOwnProperty('id') && roleData[0].hasOwnProperty('name');
                    console.log("Has standard Django group properties:", hasDjangoProps);
                    
                    if (!hasDjangoProps) {
                        console.log("Role properties available:", Object.keys(roleData[0]));
                        
                        // If we don't have expected properties, try to fetch Django groups directly
                        try {
                            console.log("Attempting to fetch Django groups directly as fallback...");
                            const groupsResponse = await roleService.fetchDjangoGroups();
                            if (groupsResponse && groupsResponse.length > 0) {
                                console.log("Successfully fetched Django groups directly:", groupsResponse);
                                return groupsResponse;
                            }
                        } catch (groupError) {
                            console.error("Failed to fetch Django groups as fallback:", groupError);
                        }
                    }
                }
                
                return roleData;
            }
            
            return response.data;
        } catch (error) {
            console.error("Error in fetchRoles:", error);
            
            // Try to fetch Django groups directly as fallback
            try {
                console.log("Attempting to fetch Django groups directly as fallback after error...");
                const groupsResponse = await roleService.fetchDjangoGroups();
                return groupsResponse || [];
            } catch (groupError) {
                console.error("Failed to fetch Django groups as fallback:", groupError);
                return [];
            }
        }
    },

    // New method to directly fetch Django auth groups
    fetchDjangoGroups: async () => {
        try {
            console.log("Fetching Django auth groups directly...");
            // This endpoint should be configured in your Django backend to return auth groups
            const response = await axiosInstance.get('django-groups/');
            console.log("Django groups response:", response);
            
            if (response && response.data) {
                const groups = Array.isArray(response.data) ? response.data : 
                    (response.data.results || []);
                    
                console.log(`Found ${groups.length} Django groups`);
                
                // Log the first group for debugging
                if (groups.length > 0) {
                    console.log("First group example:", groups[0]);
                }
                
                return groups;
            }
            
            return [];
        } catch (error) {
            console.error("Error fetching Django groups:", error);
            return [];
        }
    },

    // Add new method to fetch users by roles
    fetchUsersByRoles: async (roles) => {
        const response = await axiosInstance.get('audit-users/by-roles/', {
            params: { roles: roles.join(',') }
        });
        return response.data;
    },

    createRole: async (roleData) => {
        const response = await axiosInstance.post('audit-roles/', roleData);
        return response.data;
    },

    updateRole: async (roleId, roleData) => {
        const response = await axiosInstance.put(`audit-roles/${roleId}/`, roleData);
        return response.data;
    },

    deleteRole: async (roleId) => {
        const response = await axiosInstance.delete(`audit-roles/${roleId}/`);
        return response.data;
    },

    // Audit Users
    fetchUsers: async () => {
        const response = await axiosInstance.get('audit-users/');
        return response.data;
    },

    fetchUsersByAudit: async (auditId) => {
        const response = await axiosInstance.get('audit-users/', {
            params: { audit_id: auditId }
        });
        return response.data;
    },

    fetchUserById: async (userId) => {
        const response = await axiosInstance.get(`audit-users/${userId}/`);
        return response.data;
    },

    createUser: async (userData) => {
        const response = await axiosInstance.post('audit-users/', userData);
        return response.data;
    },

    updateUser: async (userId, userData) => {
        const response = await axiosInstance.put(`audit-users/${userId}/`, userData);
        return response.data;
    },

    deleteUser: async (userId, projectId) => {
        try {
            const response = await axiosInstance.delete(`audit-users/${userId}/?project_id=${projectId}`);
            return response.data;
        } catch (error) {
            throw error;
        }
    },

    // User Status Management
    activateUser: async (userId) => {
        const response = await axiosInstance.post(`audit-users/${userId}/activate/`);
        return response.data;
    },

    deactivateUser: async (userId) => {
        const response = await axiosInstance.post(`audit-users/${userId}/deactivate/`);
        return response.data;
    },

    // Role Assignment
    updateUserRole: async (userId, roleId) => {
        const response = await axiosInstance.post(`audit-users/${userId}/update_role/`, { role_id: roleId });
        return response.data;
    },

    // Audit Access Management
    updateUserAuditAccess: async (userId, auditIds) => {
        const response = await axiosInstance.post(`audit-users/${userId}/update_audit_access/`, { audit_ids: auditIds });
        return response.data;
    },

    fetchRolesByID: async (roleId) => {
        const response = await axiosInstance.get(`roles/${roleId}/`);
        return response.data;
    },

    createRoles: async (roleData) => {
        const response = await axiosInstance.post(`roles/`, roleData);
        return response.data;
    },

    assignPermissions: async (permissionData) => {
        const response = await axiosInstance.post(`roles/`, permissionData);
        return response.data;
    },

    updateRoles: async (roleId, roleData) => {
        const response = await axiosInstance.put(`roles/${roleId}/`, roleData);
        return response.data;
    },

    deleteRoles: async (roleId) => {
        const response = await axiosInstance.delete(`roles/${roleId}/`);
        return response.data;
    },

    getSystemPassword: async() => {
        const response = await axiosInstance.get(`systemsettings/`);
        return response.data;
    },

    createSystemPassword: async(passwordData) => {
        const response = await axiosInstance.post(`systemsettings/`, passwordData);
        return response.data;
    },

    updateSystemPassword: async(id,passwordData) => {
        const response = await axiosInstance.put(`systemsettings/${id}/`, passwordData);
        return response.data;
    },

    //ROLE Owner Services
    fetchRoleOwners: async () => {
        const response = await axiosInstance.get(`roleowners/`);
        return response.data;
    },

     //ROLE Owner Services
     fetchRoleOwnersByApp: async (appName) => {
        const response = await axiosInstance.get(`role-owners/${appName}/`);
        return response.data;
    },
    
    fetchRoleOwnerById: async (appName, roleName) => {
        const response = await axiosInstance.get(`role-owners/${appName}/${roleName}/`);
        return response.data;
    },
    
    createRoleOwner: async (rolenameData) => {
        const response = await axiosInstance.post(`roleowners/`, rolenameData);
        return response.data;
      },
    
    updateRoleOwner: async (roleName, rolenameData) => {
        const response = await axiosInstance.put(`roleowners/${roleName}/`, rolenameData);
        return response.data;
      },
    
    deleteRoleOwner: async (roleName) => {
        const response = await axiosInstance.delete(`roleowners/${roleName}/`);
        return response.data;
      }
};

export default roleService;
